import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import PortalLayout from '../../components/PortalLayout';
import { adminAPI } from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminContentManagementPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [heroContent, setHeroContent] = useState('Finde die perfekten Auftragnehmer für dein Projekt\nUnsere Plattform verbindet Auftraggeber mit erfahrenen Fachleuten\nJetzt Anfrage stellen');

  const [featuresContent, setFeaturesContent] = useState('Warum unsere Plattform?\n\n⚡ Schnelle Vermittlung\nStellen Sie eine Anfrage und erhalten Sie innerhalb kürzester Zeit Angebote von qualifizierten Fachleuten.\n\n✓ Verifizierte Profis\nAlle Auftragnehmer werden von unserem Team überprüft und freigegeben, um höchste Qualität zu garantieren.\n\n💰 Transparente Preise\nKeine versteckten Gebühren. Sie kennen von Anfang an den Preis und welche Services enthalten sind.');

  const [howItWorksContent, setHowItWorksContent] = useState('So funktioniert\'s\n\n1. Anfrage stellen\nBeschreiben Sie Ihr Projekt und legen Sie ein Budget fest.\n\n2. Prüfung\nUnser Team prüft die Anfrage und gibt sie frei.\n\n3. Auftragnehmer entdecken\nQualifizierte Profis sehen Ihre Anfrage und bieten ihre Dienste an.\n\n4. Zusammenarbeit\nKontaktdaten werden nach dem Kauf der Lead übermittelt.');

  const [ctaContent, setCtaContent] = useState('Stellen Sie jetzt eine Anfrage\n\nNeue Anfrage ohne Registrierung\nSie können eine Anfrage stellen, ohne sich zu registrieren. Die Anfrage wird von unserem Team überprüft und dann für Auftragnehmer freigegeben.\n\nMit Account-Verwaltung\nRegistrieren Sie sich, um alle Ihre Anfragen zu verwalten, den Status zu verfolgen und Kontakt mit Auftragnehmern zu pflegen.');

  const [imprintContent, setImprintContent] = useState('Callcenter-Suche\nMusterstraße 123, 12345 Musterstadt\nTelefon: +49 (0) 123 456789\nE-Mail: info@callcenter-fuchs.de\n\nGeschäftsführer: Max Mustermann\n\nHandelsregister: Amtsgericht Musterstadt, HR-Nummer: 123456\nUmsatzsteuer-ID: DE123456789\n\nHaftungsausschluss: Die Inhalte unserer Website werden mit großer Sorgfalt erstellt. Wir übernehmen jedoch keine Gewähr für die Richtigkeit, Vollständigkeit und Aktualität der bereitgestellten Inhalte.');

  const [privacyContent, setPrivacyContent] = useState('Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst und informieren Sie hiermit über die Verarbeitung Ihrer Daten gemäß Datenschutz-Grundverordnung (DSGVO).\n\nErhebung von Daten\nBei der Nutzung unserer Website werden personenbezogene Daten erhoben, wenn Sie diese freiwillig über Formulare eingeben. Dies umfasst insbesondere Name, E-Mail-Adresse, Telefonnummer und weitere Angaben, die Sie uns mitteilen.\n\nVerwendung der Daten\nIhre Daten werden ausschließlich zur Erbringung unserer Dienstleistungen verwendet. Eine Weitergabe an Dritte erfolgt nicht ohne Ihre ausdrückliche Zustimmung.\n\nSpeicherung der Daten\nIhre Daten werden so lange gespeichert, wie dies für die Erbringung der Dienstleistung notwendig ist. Sie haben jederzeit das Recht, Ihre Daten zu löschen.\n\nIhre Rechte\nSie haben das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung Ihrer Daten. Sie können diese Rechte jederzeit geltend machen.\n\nKontakt zum Datenschutz\nBei Fragen zum Datenschutz kontaktieren Sie uns bitte unter datenschutz@callcenter-fuchs.de');

  const [termsContent, setTermsContent] = useState('Diese Allgemeinen Geschäftsbedingungen (AGB) regeln die Nutzung unserer Plattform Callcenter-Suche.\n\n1. Leistungsbeschreibung\nUnsere Plattform vermittelt zwischen Auftraggebern und Auftragnehmern. Wir fungieren als Vermittler und sind nicht selbst Vertragspartei der zwischen Auftraggebern und Auftragnehmern geschlossenen Verträge.\n\n2. Verantwortung des Nutzers\nDer Nutzer ist verantwortlich für die Richtigkeit der von ihm eingegebenen Daten. Verbotene Inhalte dürfen nicht hochgeladen oder verbreitet werden.\n\n3. Zahlungsbedingungen\nDie Bezahlung erfolgt nach Abschluss der Vermittlung über die auf der Plattform angegebenen Zahlungsmethoden. Alle Preise sind inkl. gesetzlicher Mehrwertsteuer.\n\n4. Haftung\nWir haften nicht für Schäden, die durch die Nutzung der Plattform entstehen, es sei denn, diese sind durch Vorsatz oder grobe Fahrlässigkeit unsererseits verursacht.\n\n5. Beendigung der Registrierung\nDie Registrierung kann jederzeit widerrufen werden. Aktive Aufträge werden nach Beendigung noch abgeschlossen.');

  const [contactContent, setContactContent] = useState('Haben Sie Fragen oder Anregungen? Wir freuen uns über Ihre Nachricht.\n\nCallcenter-Suche\nMusterstraße 123, 12345 Musterstadt\nTel: +49 (0) 123 456789\nE-Mail: info@callcenter-fuchs.de\n\nGeschäftszeiten\nMontag bis Freitag: 09:00 - 17:00 Uhr\n\nTechnischer Support\nFür technische Unterstützung kontaktieren Sie bitte: support@callcenter-fuchs.de');

  const SECTION_KEYS: Record<string, string> = {
    'Hero-Sektion': 'content_hero',
    'Features': 'content_features',
    "So funktioniert's": 'content_how_it_works',
    'Call-to-Action': 'content_cta',
    'Impressum': 'content_imprint',
    'Datenschutz': 'content_privacy',
    'AGB': 'content_terms',
    'Kontakt': 'content_contact',
  };

  const loadContent = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getSettings();
      const settings = response.data.data || {};
      if (settings.content_hero) setHeroContent(settings.content_hero);
      if (settings.content_features) setFeaturesContent(settings.content_features);
      if (settings.content_how_it_works) setHowItWorksContent(settings.content_how_it_works);
      if (settings.content_cta) setCtaContent(settings.content_cta);
      if (settings.content_imprint) setImprintContent(settings.content_imprint);
      if (settings.content_privacy) setPrivacyContent(settings.content_privacy);
      if (settings.content_terms) setTermsContent(settings.content_terms);
      if (settings.content_contact) setContactContent(settings.content_contact);
    } catch (err: any) {
      showError('Fehler beim Laden der Inhalte');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const saveContent = async (section: string, content: string) => {
    try {
      setIsSaving(true);
      const key = SECTION_KEYS[section];
      await adminAPI.setSetting(key, content);
      showSuccess(`"${section}" erfolgreich gespeichert!`);
    } catch (err: any) {
      showError(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Website Inhalte verwalten
        </Typography>
        <Typography color="textSecondary">
          Bearbeiten Sie die Texte und Inhalte auf Ihrer Website von hier aus.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 4 }}>
        Änderungen an den Inhalten werden sofort auf der Website sichtbar. Achten Sie darauf, dass alle Texte korrekt und aktuell sind.
      </Alert>

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: '1px solid #e0e0e0' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Hero-Sektion" />
          <Tab label="Features" />
          <Tab label="So funktioniert's" />
          <Tab label="Call-to-Action" />
          <Tab label="Impressum" />
          <Tab label="Datenschutz" />
          <Tab label="AGB" />
          <Tab label="Kontakt" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Hero-Sektion Text"
              value={heroContent}
              onChange={(e) => setHeroContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die Hero-Sektion"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('Hero-Sektion', heroContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Features Text"
              value={featuresContent}
              onChange={(e) => setFeaturesContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die Features-Sektion"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('Features', featuresContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="So funktioniert's Text"
              value={howItWorksContent}
              onChange={(e) => setHowItWorksContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die So funktioniert's-Sektion"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('So funktioniert\'s', howItWorksContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Call-to-Action Text"
              value={ctaContent}
              onChange={(e) => setCtaContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die Call-to-Action-Sektion"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('Call-to-Action', ctaContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Impressum Text"
              value={imprintContent}
              onChange={(e) => setImprintContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die Impressum-Seite"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('Impressum', imprintContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Datenschutz Text"
              value={privacyContent}
              onChange={(e) => setPrivacyContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die Datenschutz-Seite"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('Datenschutz', privacyContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="AGB Text"
              value={termsContent}
              onChange={(e) => setTermsContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die AGB-Seite"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('AGB', termsContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Kontakt Text"
              value={contactContent}
              onChange={(e) => setContactContent(e.target.value)}
              multiline
              rows={12}
              variant="outlined"
              helperText="Bearbeiten Sie den gesamten Text für die Kontakt-Seite"
            />
            <Button
              variant="contained"
              onClick={() => saveContent('Kontakt', contactContent)}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </TabPanel>
      </Paper>
    </PortalLayout>
  );
};

export default AdminContentManagementPage;
